import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Upload, Users, FileText, CheckCircle, AlertCircle, Loader2, Database, ArrowLeft, RefreshCw } from "lucide-react";
import { Button } from "../components/ui/button";
import { useAuth } from "../context/AuthContext";

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function AdminMigrationPage() {
  const navigate = useNavigate();
  const { isAuthenticated, user, loading, token } = useAuth();

  const [status, setStatus] = useState(null);
  const [uploading, setUploading] = useState(null);
  const [results, setResults] = useState({ users: null, posts: null });
  const [statusLoading, setStatusLoading] = useState(true);

  useEffect(() => {
    if (loading) return;
    if (!isAuthenticated || user?.role !== "admin") {
      navigate("/");
      return;
    }
    fetchStatus();
  }, [isAuthenticated, user, loading]);

  const fetchStatus = async () => {
    setStatusLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/admin/migrate/status`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.ok) setStatus(await res.json());
    } catch (e) {
      console.error(e);
    } finally {
      setStatusLoading(false);
    }
  };

  const handleUpload = async (type) => {
    const input = document.createElement("input");
    input.type = "file";
    input.accept = ".csv";
    input.onchange = async (e) => {
      const file = e.target.files[0];
      if (!file) return;

      setUploading(type);
      const formData = new FormData();
      formData.append("file", file);

      try {
        const res = await fetch(`${API_URL}/api/admin/migrate/${type}`, {
          method: "POST",
          headers: { Authorization: `Bearer ${token}` },
          body: formData,
        });
        const data = await res.json();
        if (res.ok) {
          setResults((prev) => ({ ...prev, [type]: { success: true, ...data } }));
          fetchStatus();
        } else {
          setResults((prev) => ({ ...prev, [type]: { success: false, error: data.detail } }));
        }
      } catch (err) {
        setResults((prev) => ({ ...prev, [type]: { success: false, error: err.message } }));
      } finally {
        setUploading(null);
      }
    };
    input.click();
  };

  if (loading || statusLoading) {
    return (
      <div className="min-h-screen bg-stone-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-stone-400" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pt-24 pb-16" data-testid="admin-migration-page">
      <div className="max-w-5xl mx-auto px-4">
        {/* Header */}
        <div className="flex items-center gap-3 mb-8">
          <button onClick={() => navigate("/admin")} className="p-2 rounded-lg hover:bg-stone-200 transition" data-testid="back-to-admin-btn">
            <ArrowLeft className="w-5 h-5 text-stone-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-stone-800">WordPress 데이터 마이그레이션</h1>
            <p className="text-sm text-stone-500 mt-1">wp_users, wp_posts CSV 파일을 업로드하여 데이터를 이관합니다.</p>
          </div>
        </div>

        {/* Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8">
          <StatusCard
            icon={Users}
            title="사용자 현황"
            total={status?.users?.total || 0}
            migrated={status?.users?.migrated || 0}
            native={status?.users?.native || 0}
          />
          <StatusCard
            icon={FileText}
            title="게시글 현황"
            total={status?.posts?.total || 0}
            migrated={status?.posts?.migrated || 0}
            native={status?.posts?.native || 0}
          />
        </div>

        {/* Upload Sections */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-8">
          <UploadCard
            icon={Users}
            title="사용자 마이그레이션"
            description="WordPress wp_users 테이블의 CSV를 업로드합니다."
            columns="ID, user_login, user_email, display_name, user_registered, gender, birth_year, country, city, phone"
            type="users"
            uploading={uploading}
            result={results.users}
            onUpload={() => handleUpload("users")}
          />
          <UploadCard
            icon={FileText}
            title="게시글 마이그레이션"
            description="WordPress wp_posts 테이블의 CSV를 업로드합니다."
            columns="ID, post_title, post_content, post_author, post_date, post_status, category, views, likes"
            type="posts"
            uploading={uploading}
            result={results.posts}
            onUpload={() => handleUpload("posts")}
          />
        </div>

        {/* Refresh */}
        <div className="text-center">
          <Button variant="outline" onClick={fetchStatus} className="gap-2" data-testid="refresh-status-btn">
            <RefreshCw className="w-4 h-4" /> 현황 새로고침
          </Button>
        </div>
      </div>
    </div>
  );
}

function StatusCard({ icon: Icon, title, total, migrated, native }) {
  return (
    <div className="bg-white rounded-xl border border-stone-200 p-5 shadow-sm" data-testid={`status-card-${title}`}>
      <div className="flex items-center gap-3 mb-4">
        <div className="p-2 bg-stone-100 rounded-lg"><Icon className="w-5 h-5 text-stone-600" /></div>
        <h3 className="font-semibold text-stone-700">{title}</h3>
      </div>
      <div className="grid grid-cols-3 gap-3 text-center">
        <div>
          <p className="text-2xl font-bold text-stone-800">{total}</p>
          <p className="text-xs text-stone-500">전체</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-blue-600">{migrated}</p>
          <p className="text-xs text-stone-500">WP 이관</p>
        </div>
        <div>
          <p className="text-2xl font-bold text-emerald-600">{native}</p>
          <p className="text-xs text-stone-500">자체 가입</p>
        </div>
      </div>
    </div>
  );
}

function UploadCard({ icon: Icon, title, description, columns, type, uploading, result, onUpload }) {
  return (
    <div className="bg-white rounded-xl border border-stone-200 p-6 shadow-sm" data-testid={`upload-card-${type}`}>
      <div className="flex items-center gap-3 mb-3">
        <div className="p-2 bg-stone-100 rounded-lg"><Icon className="w-5 h-5 text-stone-600" /></div>
        <h3 className="font-semibold text-stone-700">{title}</h3>
      </div>
      <p className="text-sm text-stone-500 mb-3">{description}</p>
      <div className="bg-stone-50 rounded-lg p-3 mb-4">
        <p className="text-xs text-stone-400 mb-1">필요 컬럼 (CSV 헤더):</p>
        <p className="text-xs text-stone-600 font-mono break-all">{columns}</p>
      </div>

      <Button
        onClick={onUpload}
        disabled={uploading === type}
        className="w-full gap-2 bg-stone-800 hover:bg-stone-700 text-white"
        data-testid={`upload-${type}-btn`}
      >
        {uploading === type ? (
          <><Loader2 className="w-4 h-4 animate-spin" /> 업로드 중...</>
        ) : (
          <><Upload className="w-4 h-4" /> CSV 파일 업로드</>
        )}
      </Button>

      {result && (
        <div className={`mt-4 p-3 rounded-lg text-sm ${result.success ? "bg-emerald-50 text-emerald-700" : "bg-red-50 text-red-700"}`} data-testid={`result-${type}`}>
          {result.success ? (
            <div className="flex items-start gap-2">
              <CheckCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <div>
                <p className="font-medium">{result.message}</p>
                <p>가져옴: {result.imported}건 / 건너뜀: {result.skipped}건</p>
              </div>
            </div>
          ) : (
            <div className="flex items-start gap-2">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              <p>{result.error}</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
